import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    // First check if we have a stored user (from admin access token login)
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setToken(storedToken);
        setLoading(false);
        return;
      } catch (e) {
        // Invalid JSON, continue to /me check
        localStorage.removeItem('user');
      }
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error('Auth check failed:', error);
      // Only clear token on 401 (unauthorized) - not on network errors or other issues
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      }
      // For other errors (network, 500, etc.) keep the token and try again later
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Record daily login for streak tracking
    try {
      await axios.post(`${API}/gamification/record-activity`, 
        { activity_type: 'daily_login' },
        { headers: { Authorization: `Bearer ${access_token}` } }
      );
    } catch (e) {
      console.log('Streak tracking skipped:', e.message);
    }
    
    return userData;
  };

  const register = async () => {
    // ACCOUNT CREATION DISABLED: users can only be created by a super_admin.
    throw new Error('Account creation is disabled. Please contact your administrator for access.');
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Record daily login for streak tracking
    try {
      await axios.post(`${API}/gamification/record-activity`, 
        { activity_type: 'daily_login' },
        { headers: { Authorization: `Bearer ${access_token}` } }
      );
    } catch (e) {
      console.log('Streak tracking skipped:', e.message);
    }
    return userData;
  };

  const [impersonation] = useState(() => { try { return JSON.parse(localStorage.getItem('impersonation') || 'null'); } catch { return null; } });

  const startImpersonation = async (userId) => {
    const r = await axios.post(`${API}/admin/impersonate/${userId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
    localStorage.setItem('impersonation', JSON.stringify({
      adminToken: token, adminUser: localStorage.getItem('user'), userId,
      userEmail: r.data.user.email, userName: r.data.user.full_name, expiresAt: r.data.expires_at,
    }));
    localStorage.setItem('token', r.data.token);
    localStorage.removeItem('user');
    window.location.href = '/dashboard';
  };

  const stopImpersonation = async () => {
    const imp = impersonation;
    if (!imp) return;
    try {
      await axios.post(`${API}/auth/impersonation/end`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    } catch { /* session may already be over */ }
    localStorage.setItem('token', imp.adminToken);
    if (imp.adminUser) localStorage.setItem('user', imp.adminUser); else localStorage.removeItem('user');
    localStorage.removeItem('impersonation');
    window.location.href = `/admin/users/${imp.userId}/analytics`;
  };

  const logout = async () => {
    if (impersonation) return stopImpersonation();
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'org_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isInstructor = user?.role === 'instructor' || isAdmin;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      loginWithGoogle,
      handleOAuthCallback,
      logout,
      isAdmin,
      isSuperAdmin,
      isInstructor,
      isAuthenticated: !!user,
      checkAuth,
      impersonation,
      startImpersonation,
      stopImpersonation
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
